/**
 * Voice template-matching engine.
 *
 * SINGLE source of truth for "given a spoken phrase, which user template
 * does it most likely refer to?". Used by both voice template detectors:
 *
 *   detectVoiceTemplateCommand        ┐
 *     (trigger word required)         │
 *                                      │ ──► findBestTemplate (this file)
 *   detectTriggerlessTemplateCommand   │     • token-set coverage scoring
 *     (no trigger at word 0)           │     • whole-name string similarity
 *                                      │     • leading filler skip (≤5 words)
 *                                      │     • embedded trigger in 1st 10 words
 *                                      │     • score = max(token, string)
 *                                      ┘     • residual via consume-prefix walker
 *
 * Both wrappers live in `voiceCommands.js`. They differ only in
 * pre-processing (strip trigger / take leading window) and in the
 * thresholds they pass — the actual matching logic is identical.
 *
 * Sidebar dictation freezes the original first 10 session words before
 * calling these wrappers — this window is an in-segment cap, not a
 * rolling dictation window.
 *
 * Why this is a separate file: when a user reports "matching is wrong",
 * we only ever read this module. No need to ask "did you say the trigger
 * word or not?" — same engine handles both flows.
 *
 * Design rules baked into the engine:
 *   - Token-set coverage (word order ignored).
 *   - No stop-word / filler list. The consume-prefix walker only needs to
 *     know "where was the LAST template-name word?"; anything in between
 *     gets absorbed automatically.
 *   - Mostly literal tokens, with a fuzzy substring/1-edit tolerance for STT
 *     typos and a small Hebrew prefix-ה normalisation for grammatical
 *     inflection. Ultrasound is an intentional exception: spoken
 *     "ultrasound" / "you ess" / "US" are treated as equivalent so templates
 *     named "US …" match dictation that says "ultrasound …".
 *   - Whole-name string similarity is also computed and `max`-merged with
 *     the token-set score, so heavily garbled STT (e.g. "סדרת צו" vs
 *     "צילום עמוד שדרה צווארי") still matches when the wrapper allows.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Thresholds (per call site)
// ─────────────────────────────────────────────────────────────────────────────

/** Trigger-path threshold (relaxed — trigger word already signals intent).
 *  Tuned for Hebrew STT tails (e.g. "סדרת צו" vs "שדרה צווארי" ≈0.68). */
export const MIN_TEMPLATE_SCORE = 0.655;
export const MIN_TEMPLATE_SCORE_GAP = 0.014;

/** Trigger-less-path threshold (precision-first; user is not signalling intent). */
export const TRIGGERLESS_WINDOW = 10;
/** Max leading filler words to skip before scoring (e.g. "רוצה תבנית …"). */
export const TRIGGERLESS_MAX_PREFIX_SKIP = 5;
export const MIN_TRIGGERLESS_SCORE = 0.8;
export const MIN_TRIGGERLESS_TEMPLATE_TOKENS_MATCHED = 2;

// ─────────────────────────────────────────────────────────────────────────────
// Generic string utilities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalise a template name for comparison: lowercase, single-space, no
 * leading/trailing whitespace, hyphens/underscores collapsed to space.
 */
export const normalizeTemplateName = (name) => {
  if (name == null || typeof name !== "string") return "";
  return name
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
};

const normalizeTemplateNameCompact = (name) =>
  normalizeTemplateName(name).replace(/\s+/g, "");

const levenshteinDistance = (a = "", b = "") => {
  const s = String(a);
  const t = String(b);
  const m = s.length;
  const n = t.length;
  if (m === 0) return n;
  if (n === 0) return m;

  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i += 1) dp[i][0] = i;
  for (let j = 0; j <= n; j += 1) dp[0][j] = j;

  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost,
      );
    }
  }
  return dp[m][n];
};

const normalizedSimilarity = (a = "", b = "") => {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  const dist = levenshteinDistance(a, b);
  return 1 - dist / maxLen;
};

/**
 * Score how well a saved template title matches the spoken phrase.
 * Uses compact equality, substring boost, full-string similarity, and
 * word-window slices. This is the "whole-name fuzzy" branch of the
 * combined engine score — resilient to STT garbling.
 */
const scoreTemplateNameAgainstSpoken = (templateName, spoken) => {
  const name = String(templateName || "").trim();
  const sp = String(spoken || "").trim();
  if (!name || !sp) return 0;

  const spCompact = tokenize(normalizeForTokens(sp)).join("") || normalizeTemplateNameCompact(sp);
  const nmCompact = tokenize(normalizeForTokens(name)).join("") || normalizeTemplateNameCompact(name);
  if (!nmCompact || !spCompact) return 0;

  if (spCompact === nmCompact) return 1;

  if (spCompact.includes(nmCompact) || nmCompact.includes(spCompact)) {
    return Math.max(0.92, normalizedSimilarity(spCompact, nmCompact));
  }

  let best = normalizedSimilarity(spCompact, nmCompact);

  const spokenWords = tokenize(normalizeForTokens(sp));
  const nameWords = tokenize(normalizeForTokens(name));
  const sw = spokenWords.length;
  const tw = nameWords.length;

  // Spoken has at least as many words as template: slide a window over spoken.
  if (tw > 0 && sw >= tw) {
    for (let i = 0; i <= sw - tw; i += 1) {
      const sliceCompact = spokenWords.slice(i, i + tw).join("");
      best = Math.max(best, normalizedSimilarity(sliceCompact, nmCompact));
    }
  }

  // Spoken shorter than template (truncated STT): slide a window over template.
  if (tw > 0 && sw >= 2 && sw < tw) {
    for (let i = 0; i <= tw - sw; i += 1) {
      const sliceCompact = nameWords.slice(i, i + sw).join("");
      best = Math.max(best, normalizedSimilarity(spCompact, sliceCompact));
    }
  }

  return best;
};

// ─────────────────────────────────────────────────────────────────────────────
// Token-set helpers
// ─────────────────────────────────────────────────────────────────────────────

const BIDI_RE = /[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;
const NIQQUD_RE = /[\u0591-\u05C7]/g;
const PUNCT_RE = /[.,;:!?"'()[\]{}<>״׳`]/g;
const DASH_RE = /[-‐‑‒–—―_]/g;

/**
 * Multi-word STT forms → single modality token before splitting.
 * Longer patterns first. Ultrasound-only for now (Radical NRE).
 */
const MODALITY_PHRASE_ALIASES = [
  { pattern: /\byou\s+ess\b/g, replacement: "us" },
  { pattern: /\bultra\s+sound\b/g, replacement: "ultrasound" },
  { pattern: /\bu\s+s\b/g, replacement: "us" },
];

/**
 * Single-token modality synonyms → canonical short code.
 *
 * Hebrew "סיטי" is intentionally NOT aliased to "ct": templates are written
 * with "CT" ("CT בטן ואגן"), and Soniox is biased to transcribe both spoken
 * "CT" and "סי טי" as written "CT" (see backend sonioxGlobalVocabulary).
 * Matching therefore compares CT against CT — no alias needed.
 */
const MODALITY_TOKEN_ALIASES = {
  ultrasound: "us",
  us: "us",
};

function expandModalityPhrases(normalized) {
  let s = normalized;
  for (const { pattern, replacement } of MODALITY_PHRASE_ALIASES) {
    s = s.replace(pattern, replacement);
  }
  return s;
}

function canonicalizeModalityToken(tok) {
  return MODALITY_TOKEN_ALIASES[tok] || tok;
}

function normalizeForTokens(text) {
  if (!text || typeof text !== "string") return "";
  let s = text.normalize("NFKC").toLowerCase();
  s = s.replace(BIDI_RE, "");
  s = s.replace(NIQQUD_RE, "");
  s = s.replace(PUNCT_RE, " ");
  s = s.replace(DASH_RE, " ");
  s = s.replace(/\s+/g, " ").trim();
  s = expandModalityPhrases(s);
  return s;
}

function tokenize(normalizedText) {
  if (!normalizedText) return [];
  return normalizedText
    .split(/\s+/)
    .filter(Boolean)
    .map(canonicalizeModalityToken);
}

function templateTokensFor(template) {
  const norm = normalizeForTokens(template?.templateName || "");
  // Dedup so each unique template token is scored exactly once.
  return Array.from(new Set(tokenize(norm)));
}

// Token-level equivalence with a small substring + 1-edit tolerance for STT
// typos and Hebrew inflection variants (e.g. "ניגוד" vs "ניגד"). The 4-char
// floor avoids over-matching short codes like "ct".
function tokensApproximatelyEqual(a, b) {
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.length >= 4 && b.length >= 4) {
    if (a.includes(b) || b.includes(a)) return true;
    if (levenshteinDistance(a, b) <= 1) return true;
  }
  return false;
}

// Strip a Hebrew prefix article "ה" when doing so makes the token match a
// known template token (e.g. spoken "הCT" vs template "ct").
function stripHebrewArticleIfMatches(token, templateTokenSet) {
  if (token && token.length >= 3 && token.startsWith("ה")) {
    const stripped = token.slice(1);
    if (templateTokenSet.has(stripped)) return stripped;
  }
  return token;
}

function tokenSetScore(templateTokens, spokenTokens, templateTokenSet) {
  if (templateTokens.length === 0 || spokenTokens.length === 0) {
    return { score: 0, matched: 0 };
  }
  // Adjust spoken tokens for Hebrew article so they line up with template tokens.
  const adjustedSpoken = spokenTokens.map((tok) =>
    stripHebrewArticleIfMatches(tok, templateTokenSet),
  );
  const adjustedSpokenSet = new Set(adjustedSpoken);

  let matched = 0;
  for (const t of templateTokens) {
    if (adjustedSpokenSet.has(t)) {
      matched += 1;
      continue;
    }
    let fuzzyHit = false;
    for (const s of adjustedSpokenSet) {
      if (tokensApproximatelyEqual(t, s)) {
        fuzzyHit = true;
        break;
      }
    }
    if (fuzzyHit) matched += 1;
  }
  // "Fraction of template covered" rather than Jaccard, because the spoken
  // window will frequently contain clinical findings that the template name
  // can't cover — Jaccard would punish that legitimate extra content.
  return { score: matched / templateTokens.length, matched };
}

// Once we know which template matched, find the LAST raw word that
// contributed a matched template token. Everything from there forward is
// the user's residual dictation (findings).
//
// "Words in between matched template tokens come along for free" is the key
// insight that makes a stop-word list unnecessary:
//   spoken  : "CT  of  the  chest  small nodule ..."
//   template: "Chest CT"
//   matches : "CT" at idx 0, "chest" at idx 3 → consume up to idx 3
//   residual: "small nodule ..."
function computeConsumedPrefix(templateTokens, templateTokenSet, rawWords) {
  if (!templateTokens.length || !rawWords.length) return 0;
  const remaining = new Set(templateTokens);
  let lastMatchedIdx = -1;
  for (let i = 0; i < rawWords.length && remaining.size > 0; i += 1) {
    const tokens = tokenize(normalizeForTokens(rawWords[i]));
    for (const tok of tokens) {
      const adjusted = stripHebrewArticleIfMatches(tok, templateTokenSet);
      let hit = remaining.has(adjusted) ? adjusted : null;
      if (!hit) {
        for (const tplTok of remaining) {
          if (tokensApproximatelyEqual(tplTok, adjusted)) {
            hit = tplTok;
            break;
          }
        }
      }
      if (hit) {
        remaining.delete(hit);
        lastMatchedIdx = i;
        break;
      }
    }
  }
  return lastMatchedIdx + 1;
}

// ─────────────────────────────────────────────────────────────────────────────
// Unified engine entry point
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Score every template against the spoken phrase using BOTH token-set
 * coverage AND whole-name string similarity, then return the single best
 * match above the caller-provided threshold.
 *
 * Per template: `score = max(tokenScore, stringScore)`.
 *
 * @param {string} spokenPhrase  The phrase to score (with any trigger word
 *   already stripped, if applicable).
 * @param {Array<{ templateName: string, templateId?: string, content?: string }>} templates
 * @param {Object} opts
 * @param {number}  opts.minScore           Reject templates scoring below this.
 * @param {boolean} [opts.requireCoverage]  If true, require ≥2 matched
 *   tokens (or all tokens for ≤2-token templates). Recommended for
 *   trigger-less mode.
 * @param {number}  [opts.windowSize]       Restrict scoring to the first N
 *   raw words of `spokenPhrase`. Default = unlimited. Recommended for
 *   trigger-less mode (8).
 * @param {boolean} [opts.requireGap]       If true and the top candidate
 *   only beats #2 by less than {@link MIN_TEMPLATE_SCORE_GAP}, return null.
 * @returns {null | { template: object, residual: string, score: number, matched: number }}
 */
export const findBestTemplate = (spokenPhrase, templates, opts = {}) => {
  if (!spokenPhrase || typeof spokenPhrase !== "string") return null;
  if (!Array.isArray(templates) || templates.length === 0) return null;

  const {
    minScore,
    requireCoverage = false,
    windowSize = Infinity,
    requireGap = false,
  } = opts;
  if (typeof minScore !== "number") return null;

  const rawWords = spokenPhrase
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (rawWords.length === 0) return null;

  // Restrict the scoring window. The residual walker still uses the full
  // rawWords — once a template has been picked, we want to absorb any of
  // its tokens that happen to appear past the window too.
  const windowWords = Number.isFinite(windowSize)
    ? rawWords.slice(0, windowSize)
    : rawWords;
  const windowText = windowWords.join(" ");
  const windowTokens = tokenize(normalizeForTokens(windowText));

  const scored = [];
  for (const t of templates) {
    if (!t?.templateName) continue;
    const tokens = templateTokensFor(t);
    if (tokens.length === 0) continue;
    const tokenSet = new Set(tokens);

    const { score: tokenScore, matched } = tokenSetScore(
      tokens,
      windowTokens,
      tokenSet,
    );
    const stringScore = scoreTemplateNameAgainstSpoken(
      t.templateName,
      windowText,
    );
    const score = Math.max(tokenScore, stringScore);

    if (requireCoverage) {
      const coverageOk =
        matched >= MIN_TRIGGERLESS_TEMPLATE_TOKENS_MATCHED ||
        (tokens.length <= MIN_TRIGGERLESS_TEMPLATE_TOKENS_MATCHED &&
          matched === tokens.length);
      if (!coverageOk) continue;
    }
    if (score < minScore) continue;

    scored.push({ template: t, tokens, tokenSet, score, matched });
  }

  if (scored.length === 0) return null;
  scored.sort(
    (a, b) =>
      b.score - a.score ||
      b.matched - a.matched ||
      a.tokens.length - b.tokens.length,
  );

  const top = scored[0];

  // Optional ambiguity guard: when top barely beats #2, break the tie with
  // compact-form Levenshtein distance; bail out if still ambiguous. This
  // mirrors the legacy trigger-path behaviour.
  if (requireGap && scored.length > 1) {
    const second = scored[1];
    if (top.score - second.score < MIN_TEMPLATE_SCORE_GAP) {
      const spCompact = normalizeTemplateNameCompact(windowText);
      const topDist = levenshteinDistance(
        spCompact,
        normalizeTemplateNameCompact(top.template.templateName),
      );
      const secondDist = levenshteinDistance(
        spCompact,
        normalizeTemplateNameCompact(second.template.templateName),
      );
      if (topDist > secondDist) return null;
      if (topDist === secondDist) {
        const scoreTie = Math.abs(top.score - second.score) <= 1e-9;
        if (!scoreTie) return null;
      }
    }
  }

  // Residual: peel off whichever leading raw words contributed template
  // tokens. If the template matched purely via whole-name string similarity
  // (matched===0 — heavily garbled STT that still approximates the name),
  // consume the entire spoken phrase so we don't leak the garbled name into
  // findings.
  const consumedWords =
    top.matched > 0
      ? computeConsumedPrefix(top.tokens, top.tokenSet, rawWords)
      : rawWords.length;

  return {
    template: top.template,
    residual: rawWords
      .slice(consumedWords)
      .join(" ")
      .trim(),
    score: top.score,
    matched: top.matched,
  };
};

/**
 * {@link findBestTemplate} on the first {@link TRIGGERLESS_WINDOW} words, trying
 * skip=0..maxPrefixSkip leading fillers ("רוצה תבנית CT …" → match "CT …").
 * Returns the highest-scoring attempt. Residual excludes skipped prefix words.
 *
 * @param {string} spokenPhrase Full transcript (only the leading window is scored).
 * @param {Array} templates
 * @param {Object} opts Passed to {@link findBestTemplate}; may include
 *   `maxPrefixSkip` (default {@link TRIGGERLESS_MAX_PREFIX_SKIP}).
 */
export const findBestTemplateWithLeadingPrefixSkip = (
  spokenPhrase,
  templates,
  opts = {},
) => {
  if (!spokenPhrase || typeof spokenPhrase !== "string") return null;
  if (!Array.isArray(templates) || templates.length === 0) return null;

  const { maxPrefixSkip = TRIGGERLESS_MAX_PREFIX_SKIP, ...findOpts } = opts;

  const rawWords = spokenPhrase
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (rawWords.length === 0) return null;

  const maxSkip = Math.min(
    maxPrefixSkip,
    Math.max(0, rawWords.length - 1),
  );

  let best = null;
  for (let skip = 0; skip <= maxSkip; skip += 1) {
    const phraseFromSkip = rawWords.slice(skip).join(" ");
    if (!phraseFromSkip) continue;

    const result = findBestTemplate(phraseFromSkip, templates, findOpts);
    if (!result) continue;
    if (!best || result.score > best.score) {
      best = result;
    }
  }

  return best;
};
